import { signInWithRedirect } from "aws-amplify/auth";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import { AuthSocialButton } from "../../components/Auth/AuthSocialButton";
import { Background } from "../../components/Auth/Background";
import { AuthLanguageSelector } from "../../components/Auth/LanguageSelector";
import { Button } from "../../components/Button";
import Input from "../../components/Form/Input/input";
import { FormGroup, Form } from "../../components/Form/styles";
import { CustomIcons } from "../../components/Icons/Custom";
import { Box } from "../../components/Layout";
import { SocialProof } from "../../components/SocialProof";
import { useAppState } from "../../contexts/App";
import { useQueryUrl } from "../../hooks/QueryUrl";
import { useSiteRef } from "../../hooks/SiteRef";
import SymbolKeepoIcon from "../../icons/SymbolKeepo";
import * as authenticationService from "../../services/auth";

const valid_actions = ["plus", "login", "start", "detail"];

const getSignInErrorMessage = (message: string, t: (key: string) => string) => {
  const lowerCaseMessage = message.toLowerCase();

  if (lowerCaseMessage.includes("auth_token_not_found")) {
    return t("pages.login.errors.generic");
  }

  if (
    lowerCaseMessage.includes("password") ||
    lowerCaseMessage.includes("invalid_credentials") ||
    lowerCaseMessage.includes("invalid credentials") ||
    lowerCaseMessage.includes("unauthorized")
  ) {
    return t("pages.login.errors.invalidCredentials");
  }

  if (lowerCaseMessage.includes("not confirmed")) {
    return t("pages.login.errors.userNotConfirmed");
  }

  return t("pages.login.errors.generic");
};

const LoginPage = () => {
  const { t } = useTranslation();
  const queryUrl = useQueryUrl();
  const { setRef } = useSiteRef();
  const { markAuthenticated } = useAppState();
  const navigate = useNavigate();
  const [credentials, setCredentials] = useState({ email: "", password: "" });
  const [isLoading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const action = (useQueryUrl().get("action") || "start").toLowerCase();

  const messages = useMemo(() => {
    const safeAction = valid_actions.includes(action) ? action : "start";
    return {
      title: t(`pages.login.${safeAction}.title`),
      description: t(`pages.login.${safeAction}.description`),
    };
  }, [action, t]);

  const startFFederatedLogin = async (provider: "Google" | "Facebook") => {
    await signInWithRedirect({
      provider: provider,
    });
  };

  const signInWithEmailAndPassword = async () => {
    if (!credentials.email || !credentials.password || isLoading) {
      return;
    }

    setErrorMessage(undefined);
    setLoading(true);
    try {
      await authenticationService.signInWithEmailAndPassword(credentials.email, credentials.password);
      await markAuthenticated();
      navigate("/", { replace: true });
    } catch (e) {
      setErrorMessage(getSignInErrorMessage((e as Error).message, t));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setRef(queryUrl.get("ref"));
  }, [queryUrl, setRef]);

  return (
    <div className="auth-screen">
      <div className="content !w-full">
        <header className="mb-16 flex items-center justify-between 2xl:mb-0">
          <SymbolKeepoIcon />
          <AuthLanguageSelector />
        </header>
        <div className="space-y-8">
          <div className="mb-2">
            <h3 className="typography-h3 mb-1">{messages.title}</h3>
            <p className="typography-p">{messages.description}</p>
          </div>
          <Box className="!mx-0 !mt-0" borderBottom>
            <Form
              className="flex flex-col"
              onSubmit={e => {
                e.preventDefault();
                signInWithEmailAndPassword();
              }}>
              <FormGroup>
                <Input
                  onChange={e => setCredentials({ ...credentials, email: e.target.value })}
                  label={t("pages.login.form.emailLabel")}
                  value={credentials.email}
                  id="email"
                  type="email"
                  placeholder={t("pages.login.form.emailPlaceholder")}
                />
              </FormGroup>
              <FormGroup>
                <Input
                  onChange={e => setCredentials({ ...credentials, password: e.target.value })}
                  label={t("pages.login.form.passwordLabel")}
                  value={credentials.password}
                  id="password"
                  type="password"
                  placeholder={t("pages.login.form.passwordPlaceholder")}
                />
              </FormGroup>
              <div className="-mt-2 mb-6 text-right">
                <Link
                  to="/recovery-password"
                  className="typography-p text-white underline underline-offset-2 hover:text-shark-300">
                  {t("pages.login.forgotPassword")}
                </Link>
              </div>
              <Button
                onClick={signInWithEmailAndPassword}
                type="INFO"
                size="LG"
                block
                disabled={!credentials.email || !credentials.password || isLoading}>
                {isLoading ? t("pages.login.loginButton.loading") : t("pages.login.loginButton.button")}
              </Button>
              {/* The visible Button is a styled div; this lets Enter submit the form */}
              <button type="submit" className="sr-only" tabIndex={-1} aria-hidden="true" />
            </Form>
          </Box>
          {errorMessage && <p className="typography-p text-red-500">{errorMessage}</p>}
          <div className="flex flex-col gap-4">
            <AuthSocialButton onClick={() => startFFederatedLogin("Google")}>
              <CustomIcons.Google />
              {t("pages.login.loginButton.google")}
            </AuthSocialButton>
            <AuthSocialButton onClick={() => startFFederatedLogin("Facebook")}>
              <CustomIcons.Facebook />
              {t("pages.login.loginButton.facebook")}
            </AuthSocialButton>
          </div>
          <div className="mt-8">
            <p className="typography-p font-bold text-white">
              {t("pages.login.register.text")}{" "}
              <Link to="/register" className="text-white underline underline-offset-2 hover:text-shark-300">
                {t("pages.login.register.linkText")}
              </Link>
            </p>
          </div>
        </div>
        <footer className="mt-16 2xl:mt-0">
          <SocialProof />
        </footer>
      </div>
      <Background />
    </div>
  );
};

export default LoginPage;
